<template>
  <Teleport to="body">
    <Transition name="cart-overlay">
      <div 
        v-if="cartStore.isCartOpen" 
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        @click="cartStore.closeCart()"
      ></div>
    </Transition>

    <Transition name="cart-sidebar">
      <div 
        v-if="cartStore.isCartOpen"
        class="fixed right-0 top-0 h-full w-full sm:w-[450px] bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div class="flex items-center gap-3">
            <div class="relative">
              <svg class="w-7 h-7 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <span 
                v-if="cartStore.itemCount > 0"
                class="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse"
              >
                {{ cartStore.itemCount }}
              </span>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
              My Cart
            </h2>
          </div>
          <button 
            @click="cartStore.closeCart()"
            class="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <svg class="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Cart Items -->
        <div class="flex-1 overflow-y-auto p-6 space-y-4">
          <!-- Empty State -->
          <div v-if="cartStore.items.length === 0" class="flex flex-col items-center justify-center h-full text-center py-12">
            <div class="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
              <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Your cart is empty
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-6">
              Add products to start your purchase
            </p>
            <button 
              @click="cartStore.closeCart()"
              class="px-6 py-3 font-semibold rounded-xl hover:scale-105 transition-transform duration-300 cursor-pointer"
              style="background-color: #EDD97A; color: #1a1200;"
            >
              Explore Products
            </button>
          </div>

          <!-- Cart Items List -->
          <TransitionGroup name="cart-item" tag="div" class="space-y-4">
            <div 
              v-for="item in cartStore.items" 
              :key="item.id"
              class="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 flex gap-4 group hover:shadow-lg transition-all duration-300"
            >
              <!-- Product Image -->
              <div class="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                <img 
                  :src="item.image" 
                  :alt="item.name"
                  class="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-300"
                />
              </div>

              <!-- Product Info -->
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-gray-900 dark:text-white mb-1 truncate">
                  {{ item.name }}
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-3 truncate">
                  {{ item.description }}
                </p>
                
                <!-- Quantity Controls -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-lg p-1">
                    <button 
                      @click="cartStore.updateQuantity(item.id, item.quantity - 1)"
                      class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    >
                      <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                      </svg>
                    </button>
                    <span class="w-8 text-center font-semibold text-gray-900 dark:text-white">
                      {{ item.quantity }}
                    </span>
                    <button 
                      @click="cartStore.updateQuantity(item.id, item.quantity + 1)"
                      class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    >
                      <svg class="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  
                  <div class="text-right">
                    <p class="text-lg font-bold text-gray-900 dark:text-white">
                      ${{ (item.price * item.quantity).toFixed(2) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Remove Button -->
              <button 
                @click="cartStore.removeItem(item.id)"
                class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0 cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </TransitionGroup>
        </div>

        <!-- Footer -->
        <div v-if="cartStore.items.length > 0" class="border-t border-gray-200 dark:border-gray-800 p-6 space-y-4 bg-gray-50 dark:bg-gray-800/50">
          <!-- Subtotal -->
          <div class="flex items-center justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
            <span class="font-semibold text-gray-900 dark:text-white">${{ cartStore.totalPrice.toFixed(2) }}</span>
          </div>
          
          <!-- Shipping -->
          <div class="flex items-center justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Shipping</span>
            <span v-if="cartStore.totalPrice >= 45" class="font-semibold text-green-600 dark:text-green-400">FREE</span>
            <span v-else class="font-semibold text-gray-900 dark:text-white">$7.00</span>
          </div>

          <!-- Free shipping progress -->
          <div v-if="cartStore.totalPrice < 45" class="text-xs text-gray-500 dark:text-gray-400">
            Add ${{ (45 - cartStore.totalPrice).toFixed(2) }} more for free shipping
          </div>

          <!-- Total -->
          <div class="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <span class="text-lg font-bold text-gray-900 dark:text-white">Total</span>
            <span class="text-2xl font-bold text-gray-900 dark:text-white">${{ (cartStore.totalPrice >= 45 ? cartStore.totalPrice : cartStore.totalPrice + 7).toFixed(2) }}</span>
          </div>

          <!-- Checkout Button -->
          <button 
            @click="handleCheckout"
            class="w-full py-4 font-bold rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-2xl flex items-center justify-center gap-2 cursor-pointer"
            style="background-color: #EDD97A; color: #1a1200;"
          >
            <span>Proceed to Checkout</span>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>

          <!-- Continue Shopping -->
          <button 
            @click="cartStore.closeCart()"
            class="w-full py-3 font-semibold transition-colors cursor-pointer"
            style="color: #5f4842;"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useCartStore } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import { useModalStore } from '@/stores/modal'

const router = useRouter()
const cartStore = useCartStore()
const userStore = useUserStore()
const modalStore = useModalStore()

const handleCheckout = () => {
  // Verificar si el usuario está autenticado
  if (!userStore.isAuthenticated) {
    // Cerrar el carrito y abrir el modal de login
    cartStore.closeCart()
    modalStore.openLoginModal()
    return
  }
  
  // Si está autenticado, proceder al checkout
  cartStore.closeCart()
  router.push('/checkout')
}
</script>

<style scoped>
/* Overlay Animation */
.cart-overlay-enter-active,
.cart-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.cart-overlay-enter-from,
.cart-overlay-leave-to {
  opacity: 0;
}

/* Sidebar Animation */
.cart-sidebar-enter-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.cart-sidebar-leave-active {
  transition: transform 0.3s cubic-bezier(0.7, 0, 0.84, 0);
}

.cart-sidebar-enter-from,
.cart-sidebar-leave-to {
  transform: translateX(100%);
}

/* Cart Item Animation */
.cart-item-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.cart-item-leave-active {
  transition: all 0.3s ease;
}

.cart-item-enter-from {
  opacity: 0;
  transform: translateX(30px) scale(0.95);
}

.cart-item-leave-to {
  opacity: 0;
  transform: translateX(-30px) scale(0.95);
}

.cart-item-move {
  transition: transform 0.3s ease;
}
</style>

